import React from 'react';
import { useNavigate } from 'react-router-dom';
import './PoliticaDePrivacidade.css';

const PoliticaDePrivacidade: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="privacidade-page">
      <header className="privacidade-header">
        <div className="privacidade-header-content">
          <div className="privacidade-logo" onClick={() => navigate('/')}>
            <img src="/logo_transp.png" alt="ContratoSeguro" />
          </div>
          <button onClick={() => navigate('/')} className="btn-back">
            Voltar para o início
          </button>
        </div>
      </header>

      <main className="privacidade-main">
        <div className="privacidade-container">
          <div className="privacidade-content">
            <h1>POLÍTICA DE PRIVACIDADE — CONTRATOSEGURO</h1>
            <p className="privacidade-updated">Última atualização: 6 de fevereiro de 2026</p>

            <p className="privacidade-intro">
              O <strong>ContratoSeguro</strong> respeita a sua privacidade e está comprometido
              com a proteção dos dados pessoais de seus usuários, em conformidade com a{' '}
              <strong>Lei Geral de Proteção de Dados (Lei nº 13.709/2018 – LGPD)</strong>.
            </p>

            <p className="privacidade-intro">
              Esta Política de Privacidade explica, de forma clara e transparente, como coletamos,
              utilizamos, armazenamos, compartilhamos e protegemos seus dados pessoais ao utilizar
              nossa plataforma.
            </p>

            <section className="privacidade-section">
              <h2>1. Quem somos</h2>
              <p>
                <strong>ContratoSeguro</strong> é uma plataforma digital que utiliza inteligência
                artificial para analisar contratos enviados pelos usuários, oferecendo resumos,
                explicações e alertas de forma automatizada, atualmente com foco em contratos de
                locação residencial.
              </p>
            </section>

            <section className="privacidade-section">
              <h2>2. Dados pessoais que coletamos</h2>
              <h3>2.1. Dados fornecidos diretamente pelo usuário</h3>
              <p>Podemos coletar os seguintes dados:</p>
              <ul>
                <li>Nome completo</li>
                <li>Endereço de e-mail</li>
                <li>Senha (armazenada de forma criptografada)</li>
                <li>Informações inseridas no cadastro</li>
                <li>Documentos enviados para análise (contratos)</li>
                <li>Perguntas feitas à plataforma sobre os documentos</li>
              </ul>

              <h3>2.2. Dados coletados automaticamente</h3>
              <p>Durante o uso da plataforma, podemos coletar:</p>
              <ul>
                <li>Endereço IP</li>
                <li>Tipo de dispositivo e navegador</li>
                <li>Dados de acesso e navegação</li>
                <li>Cookies e tecnologias semelhantes</li>
              </ul>

              <h3>2.3. Dados das partes do contrato (PII extraída do documento)</h3>
              <p>
                Ao enviar contratos, extraímos e armazenamos dados pessoais identificáveis das partes
                envolvidas (ex.: locador, locatário, fiadores e testemunhas), tais como:
              </p>
              <ul>
                <li>Nome, CPF/CNPJ e dados de identificação presentes no documento</li>
                <li>Endereço, e-mail e telefone informados no contrato</li>
                <li>Assinaturas e qualificações das partes ou de seus representantes</li>
                <li>Demais informações necessárias para mapear obrigações e comunicações contratuais</li>
              </ul>
              <p>
                Esses dados são tratados para viabilizar a análise do contrato, gerar resumos,
                manter o histórico, sugerir obrigações e disparar notificações vinculadas ao documento,
                sempre de forma segura e alinhada às finalidades descritas nesta política.
              </p>
            </section>

            <section className="privacidade-section">
              <h2>3. Finalidade do tratamento dos dados</h2>
              <p>Utilizamos seus dados pessoais para:</p>
              <ul>
                <li>Criar e gerenciar sua conta de usuário</li>
                <li>Permitir o envio e a análise automatizada de contratos</li>
                <li>Exibir resultados personalizados</li>
                <li>Armazenar documentos na sua biblioteca pessoal</li>
                <li>Registrar partes, obrigações e responsabilidades identificadas em cada contrato</li>
                <li>Enviar notificações relacionadas às partes e aos prazos do contrato</li>
                <li>Melhorar a performance e a experiência da plataforma</li>
                <li>Garantir a segurança e prevenção a fraudes</li>
                <li>Cumprir obrigações legais e regulatórias</li>
              </ul>
            </section>

            <section className="privacidade-section">
              <h2>4. Bases legais para o tratamento</h2>
              <p>
                O tratamento de dados pessoais pelo ContratoSeguro ocorre com base nas seguintes
                hipóteses legais previstas na LGPD:
              </p>
              <ul>
                <li><strong>Execução de contrato</strong> (art. 7º, V)</li>
                <li><strong>Consentimento do titular</strong> (art. 7º, I)</li>
                <li><strong>Cumprimento de obrigação legal</strong> (art. 7º, II)</li>
                <li>
                  <strong>Legítimo interesse</strong> (art. 7º, IX), respeitando os direitos do titular
                </li>
              </ul>
              <p>
                Ao enviar documentos com dados pessoais de terceiros, você declara ter obtido
                consentimento ou possuir outra base legal válida para compartilhá-los com o
                ContratoSeguro e autorizar seu tratamento conforme descrito nesta política.
              </p>
            </section>

            <section className="privacidade-section">
              <h2>5. Uso e tratamento dos documentos enviados</h2>
              <ul>
                <li>São processados de forma <strong>automatizada</strong> por sistemas de IA.</li>
                <li>São utilizados <strong>exclusivamente</strong> para análise e exibição ao próprio usuário.</li>
                <li><strong>Não são utilizados para treinar modelos de IA externos</strong>.</li>
                <li><strong>Não são vendidos ou compartilhados com terceiros</strong>.</li>
                <li>Podem ter dados estruturados das partes (ex.: nome, CPF/CNPJ, contato) armazenados para resumos, histórico e notificações.</li>
                <li>São armazenados de forma segura e podem ser excluídos a qualquer momento pelo usuário.</li>
              </ul>
            </section>

            <section className="privacidade-section">
              <h2>6. Compartilhamento de dados</h2>
              <p>O ContratoSeguro <strong>não comercializa dados pessoais</strong>.</p>
              <p>Os dados poderão ser compartilhados apenas com:</p>
              <ul>
                <li>Provedores de infraestrutura e hospedagem (ex.: serviços de nuvem)</li>
                <li>Serviços de autenticação e segurança</li>
                <li>Ferramentas de monitoramento e análise de desempenho (analytics), quando aplicável</li>
              </ul>
              <p>
                Todos os parceiros seguem padrões adequados de segurança e proteção de dados. Dados
                das partes do contrato são compartilhados apenas quando estritamente necessário para
                a execução do serviço (ex.: armazenamento seguro ou envio de notificações).
              </p>
            </section>

            <section className="privacidade-section">
              <h2>7. Armazenamento e segurança dos dados</h2>
              <p>Adotamos medidas técnicas e administrativas para proteger os dados pessoais, incluindo:</p>
              <ul>
                <li>Criptografia de dados em repouso e em trânsito</li>
                <li>Uso de conexões seguras (HTTPS/TLS)</li>
                <li>Controle de acesso restrito</li>
                <li>Monitoramento de segurança</li>
                <li>Backups regulares</li>
              </ul>
              <p>Apesar de adotarmos boas práticas, nenhum sistema é completamente livre de riscos.</p>
            </section>

            <section className="privacidade-section">
              <h2>8. Retenção dos dados</h2>
              <ul>
                <li><strong>Dados da conta:</strong> enquanto a conta estiver ativa</li>
                <li><strong>Documentos enviados e dados das partes:</strong> até que o usuário solicite a exclusão ou remova o contrato</li>
                <li><strong>Logs e registros técnicos:</strong> pelo prazo necessário para segurança e cumprimento legal</li>
              </ul>
              <p>
                Dados poderão ser retidos por períodos adicionais quando exigido por lei. Ao excluir
                um documento, removemos o arquivo e os dados estruturados associados às partes do contrato.
              </p>
            </section>

            <section className="privacidade-section">
              <h2>9. Direitos do titular dos dados</h2>
              <p>Nos termos da LGPD, o usuário possui os seguintes direitos:</p>
              <ul>
                <li>Confirmar a existência de tratamento de dados</li>
                <li>Acessar seus dados pessoais</li>
                <li>Corrigir dados incompletos, inexatos ou desatualizados</li>
                <li>Solicitar anonimização, bloqueio ou exclusão</li>
                <li>Solicitar portabilidade dos dados</li>
                <li>Revogar o consentimento</li>
                <li>Solicitar informações sobre compartilhamento</li>
                <li>Reclamar junto à Autoridade Nacional de Proteção de Dados (ANPD)</li>
              </ul>
            </section>

            <section className="privacidade-section">
              <h2>10. Como exercer seus direitos</h2>
              <p>
                Para exercer seus direitos, o usuário pode entrar em contato pelo e-mail:
                <br />
                📧 <strong>privacidade@contratoseguro.com</strong>
              </p>
              <p>Responderemos às solicitações dentro dos prazos previstos em lei.</p>
              <p>
                Ao solicitar exclusão ou anonimização, também removeremos os registros estruturados
                relacionados às partes identificadas em seus contratos, salvo retenções exigidas por lei.
              </p>
            </section>

            <section className="privacidade-section">
              <h2>11. Cookies e tecnologias semelhantes</h2>
              <p>Utilizamos cookies para:</p>
              <ul>
                <li>Garantir o funcionamento adequado da plataforma</li>
                <li>Melhorar a experiência do usuário</li>
                <li>Analisar desempenho e uso do serviço</li>
              </ul>
              <p>
                O usuário pode gerenciar cookies diretamente em seu navegador. Para mais informações,
                consulte nossa <strong>Política de Cookies</strong>.
              </p>
            </section>

            <section className="privacidade-section">
              <h2>12. Transferência internacional de dados</h2>
              <p>
                Caso utilizemos serviços de armazenamento ou processamento fora do Brasil, garantimos
                que tais transferências ocorrerão em conformidade com a LGPD, adotando medidas adequadas
                de proteção.
              </p>
            </section>

            <section className="privacidade-section">
              <h2>13. Alterações desta política</h2>
              <p>Esta Política de Privacidade pode ser atualizada periodicamente.</p>
              <p>A versão mais recente estará sempre disponível na Plataforma.</p>
              <p>O uso contínuo do ContratoSeguro após alterações implica concordância com os novos termos.</p>
            </section>

            <section className="privacidade-section">
              <h2>14. Encarregado pelo tratamento de dados (DPO)</h2>
              <p>
                O Encarregado pelo tratamento de dados pessoais pode ser contatado pelo e-mail:
                <br />
                📧 <strong>privacidade@contratoseguro.com</strong>
              </p>
            </section>

            <section className="privacidade-section">
              <h2>15. Contato</h2>
              <p>Para dúvidas, solicitações ou esclarecimentos:</p>
              <p>
                📧 <strong>contato@contratoseguro.com</strong>
              </p>
            </section>

            <div className="privacidade-acceptance">
              <p><strong>ContratoSeguro</strong></p>
              <p><em>Clareza, segurança e respeito aos seus dados.</em></p>
            </div>
          </div>
        </div>
      </main>

      <footer className="privacidade-footer">
        <div className="privacidade-footer-content">
          <p>&copy; 2026 ContratoSeguro. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default PoliticaDePrivacidade;
